/* eslint-disable import/prefer-default-export */
import type {
  IdentityInfo,
  InstanceTileInfo,
  MemberTileInfo,
  PrivilegeTileInfo,
  RoleTileInfo,
  ScopeTileInfo,
  UserTileInfo
} from '@src/endpoints/services/graphQueryMgr.service'
import type { InfoDetailsResult } from '@src/utils/resource.utils'
import type {
  IdentitiesState,
  IdentityTileUsedTotalValues,
  ObjectItem
} from '@src/redux/reducers/identities/default.state'
import type { AuthState } from '@src/redux/reducers/auth'
import {
  createScopeInstance,
  getScopeDisplayName
} from '@src/utils/scope.utils'
import { isEmpty } from 'lodash'
import { parseScopeList } from '@src/redux/reducers/identities/helpers'

export interface TileDataType {
  objectList: ObjectItem[]
  priviligeList?: PrivilegeTileInfo[]
  roleList?: RoleTileInfo[]
  memberList?: MemberTileInfo[]
  instanceList?: InstanceTileInfo[]
  userList?: UserTileInfo[]
  scopeList?: ScopeTileInfo[]
}

export const parseIndentityData = (
  data: IdentityInfo,
  infoDetails: InfoDetailsResult,
  auth: AuthState
): IdentitiesState => {
  const dataInfo = {} as IdentitiesState

  const {
    user_info,
    group_info,
    role_info,
    scope_info,
    service_account_info,
    used_object_count,
    object_count
  } = data

  const objects: IdentityTileUsedTotalValues = {
    used: used_object_count || 0,
    total: object_count || 0
  }
  if (!isEmpty(infoDetails.Name)) {
    dataInfo.displayName = getScopeDisplayName(
      createScopeInstance(infoDetails?.Name[0]).parse(auth).value
    )
  }

  if (!isEmpty(user_info)) {
    dataInfo.countOfUTPrivileges = {
      used: user_info?.priv_used || 0,
      total: user_info?.priv_total || 0
    }
    dataInfo.membership = {
      roles: user_info?.roles_count || 0,
      rolesUsed: user_info?.used_roles_count || 0,
      groups: user_info?.groups_count || 0
    }
    dataInfo.priviligeList = user_info?.privilege_tile_info || []
    dataInfo.roleList = user_info?.role_tile_info || []
  }
  if (!isEmpty(group_info)) {
    dataInfo.countOfPrivileges = group_info?.privileges || 0
    dataInfo.countOfMembers = group_info?.members || 0
    dataInfo.priviligeList = group_info?.privilege_tile_info || []
    dataInfo.memberList = group_info?.member_tile_info || []
  }
  if (!isEmpty(role_info)) {
    dataInfo.countOfScopes = {
      used: role_info?.scope_used || 0,
      total: role_info?.scope_total || 0
    }

    dataInfo.countOfUTPrivileges = {
      used: role_info?.priv_used || 0,
      total: role_info?.priv_total || 0
    }
    dataInfo.countOfInstances = {
      used: role_info?.instances_used || 0,
      total: role_info?.instances_total || 0
    }
    dataInfo.countOfUsers = {
      used: role_info?.users_used || 0,
      total: role_info?.users_total || 0
    }
    dataInfo.userList = role_info?.user_tile_info || []
    dataInfo.instanceList = role_info?.instance_tile_info || []
    dataInfo.priviligeList = role_info?.privilege_tile_info || []
    dataInfo.scopeList = role_info?.scope_tile_info || []
  }

  if (!isEmpty(scope_info)) {
    dataInfo.displayName = scope_info?.role_name
    dataInfo.displayScopeName = getScopeDisplayName(
      createScopeInstance(scope_info?.scope).parse(auth).value
    )
    dataInfo.countOfUTPrivileges = {
      used: scope_info?.priv_used || 0,
      total: scope_info?.priv_total || 0
    }
    dataInfo.countOfInstances = {
      used: scope_info?.instances_used || 0,
      total: scope_info?.instances_total || 0
    }
    dataInfo.countOfUsers = {
      used: scope_info?.users_used || 0,
      total: scope_info?.users_total || 0
    }
    dataInfo.userList = scope_info?.user_tile_info || []
    dataInfo.instanceList = scope_info?.instance_tile_info || []
    dataInfo.priviligeList = scope_info?.privilege_tile_info || []
  }

  if (!isEmpty(service_account_info)) {
    dataInfo.countOfUTPrivileges = {
      used: service_account_info?.priv_used || 0,
      total: service_account_info?.priv_total || 0
    }
    dataInfo.membership = {
      groups: service_account_info?.groups_count || 0,
      roles: service_account_info?.roles_count || 0,
      rolesUsed: service_account_info?.used_roles_count || 0
    }
    dataInfo.priviligeList = service_account_info?.privilege_tile_info || []
    dataInfo.roleList = service_account_info?.role_tile_info || []
  }

  return { ...dataInfo, objects, infoDetails } as IdentitiesState
}

export const parseIndentityDetailsStats = (
  data: IdentityInfo,
  infoDetails: InfoDetailsResult,
  auth: AuthState
): IdentitiesState => {
  const dataInfo = {} as IdentitiesState

  const {
    user_info,
    group_info,
    role_info,
    scope_info,
    service_account_info,
    used_object_count,
    object_count
  } = data
  const objects: IdentityTileUsedTotalValues = {
    used: used_object_count || 0,
    total: object_count || 0
  }
  const infoDetailsData = infoDetails
  if (!isEmpty(infoDetailsData.Name)) {
    // Parse name
    const scopeName = createScopeInstance(infoDetailsData?.Name[0]).parse(
      auth
    ).value
    infoDetailsData.Name[0] = scopeName
    dataInfo.displayName = getScopeDisplayName(scopeName)
  }

  if (!isEmpty(user_info)) {
    dataInfo.countOfUTPrivileges = {
      used: user_info?.priv_used || 0,
      total: user_info?.priv_total || 0
    }
    dataInfo.membership = {
      roles: user_info?.roles_count || 0,
      rolesUsed: user_info?.used_roles_count || 0,
      groups: user_info?.groups_count || 0
    }
  }
  if (!isEmpty(group_info)) {
    dataInfo.countOfPrivileges = group_info?.privileges || 0
    dataInfo.countOfMembers = group_info?.members || 0
  }
  if (!isEmpty(role_info)) {
    dataInfo.countOfScopes = {
      used: role_info?.scope_used || 0,
      total: role_info?.scope_total || 0
    }

    dataInfo.countOfUTPrivileges = {
      used: role_info?.priv_used || 0,
      total: role_info?.priv_total || 0
    }
    dataInfo.countOfInstances = {
      used: role_info?.instances_used || 0,
      total: role_info?.instances_total || 0
    }
    dataInfo.countOfUsers = {
      used: role_info?.users_used || 0,
      total: role_info?.users_total || 0
    }
  }

  if (!isEmpty(scope_info)) {
    dataInfo.displayName = scope_info?.role_name
    dataInfo.displayScopeName = getScopeDisplayName(
      createScopeInstance(scope_info?.scope).parse(auth).value
    )
    dataInfo.countOfUTPrivileges = {
      used: scope_info?.priv_used || 0,
      total: scope_info?.priv_total || 0
    }
    dataInfo.countOfInstances = {
      used: scope_info?.instances_used || 0,
      total: scope_info?.instances_total || 0
    }
    dataInfo.countOfUsers = {
      used: scope_info?.users_used || 0,
      total: scope_info?.users_total || 0
    }
  }

  if (!isEmpty(service_account_info)) {
    dataInfo.countOfUTPrivileges = {
      used: service_account_info?.priv_used || 0,
      total: service_account_info?.priv_total || 0
    }
    dataInfo.membership = {
      groups: service_account_info?.groups_count || 0,
      roles: service_account_info?.roles_count || 0,
      rolesUsed: service_account_info?.used_roles_count || 0
    }
  }
  const identitiesState = {
    ...dataInfo,
    objects,
    infoDetails: infoDetailsData
  }
  return {
    ...identitiesState,
    scopeList: parseScopeList(identitiesState, auth)
  } as IdentitiesState
}

export const parseIndentityDetailsTileData = (
  data: IdentityInfo
): IdentitiesState => {
  const dataInfo = {} as TileDataType

  const {
    privilege_tile_info,
    role_tile_info,
    member_tile_info,
    user_tile_info,
    instance_tile_info,
    scope_tile_info
  } = data

  dataInfo.priviligeList = privilege_tile_info || []
  dataInfo.roleList = role_tile_info || []
  dataInfo.memberList = member_tile_info || []
  dataInfo.userList = user_tile_info || []
  dataInfo.instanceList = instance_tile_info || []
  dataInfo.scopeList = scope_tile_info || []

  return dataInfo as IdentitiesState
}
